import { AfterViewInit, Component, ElementRef, inject, Injector, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { faCalendar, faCloud, faEnvelope, faFlag, faFolderPlus, faHourglassEnd, faKeyboard, faRotateLeft, faTableList } from '@fortawesome/free-solid-svg-icons';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { ClassicPreset, GetSchemes } from 'rete';
import { Node } from 'rete/_types/presets/classic';
import { catchError, of } from 'rxjs';
import { ServerProgressBarSpinnerComponent } from 'src/app/controls/server-progress-bar-spinner/server-progress-bar-spinner.component';
import { HttpError } from 'src/app/core/http-error';
import { ServiceCodes } from 'src/app/models/enums/service-codes';
import { WorkflowActionTypes } from 'src/app/models/RestApiCaller/Enums/workflow-action-types';
import { WorkflowTriggerTypes } from 'src/app/models/RestApiCaller/Enums/workflow-trigger-types';
import { MemoryService } from 'src/app/services/memory.service';
import { NotificationService } from 'src/app/services/notification-service';
import { WorkflowDefinitionsService } from 'src/app/services/workflows/workflow-definitions.service';
import { SelectableConnection } from './connections/models/SelectableConnection';
import { DesignerCallbackHandler } from './core/designer-callback-handler';
import { EditorWrapper } from './core/editor-wrapper';
import { MappingInfos } from './core/mapping-infos';
import { NodesManager } from './core/nodes-manager';
import { SelectionHandler } from './core/selection-handler';
import { OrderingStepDialogData } from './dialogs/ordering-step-dialog/models/ordering-step-dialog-data';
import { OrderingStepDialogComponent } from './dialogs/ordering-step-dialog/ordering-step-dialog.component';
import { SearchDialogResult } from './dialogs/search-dialog/models/search-dialog-result';
import { SearchDialogComponent } from './dialogs/search-dialog/search-dialog.component';
import { WorkflowSettingsDialogData } from './dialogs/workflow-settings-dialog/models/workflow-settings-dialog-data';
import { WorkflowSettingsDialogComponent } from './dialogs/workflow-settings-dialog/workflow-settings-dialog.component';
import { Action } from './models/action';
import { ActivatedStepsConditionData } from './models/activated-steps-condition-data';
import { AttributeChangedTriggerData } from './models/attribute-change-trigger-data';
import { CompletedStepsConditionData } from './models/completed-steps-condition-data';
import { ElementVisibility } from './models/element-visibilty';
import { ElementVisibilityTypes } from './models/Enums/element-visibility-types';
import { WorkflowConditionTypes } from './models/Enums/workflow-condition-types';
import { WorkflowElementType } from './models/Enums/workflow-element-type';
import { Event } from './models/event';
import { EventData } from './models/event-data';
import { ExpiredDelayTriggerData } from './models/expire-delay-trigger-data';
import { ReteContext } from './models/rete-context';
import { Step } from './models/step';
import { StepData } from './models/step-data';
import { StepSpecificStateConditionData } from './models/step-specific-state-condition-datas';
import { TimerStopActionData } from './models/timer-stop-action-data';
import { TimerTriggerData } from './models/timer-trigger-data';
import { Trigger } from './models/trigger';
import { AnyActionData } from './models/Types/any-action-data';
import { AnyTriggerData } from './models/Types/any-trigger-data';
import { Workflow } from './models/workflow';
import { WorkflowValidityReport } from './models/workflow-validity-report';
import { WorkflowActionNode } from './nodes/models/workflow-action-node';
import { WorkflowStepNode } from './nodes/models/workflow-step-node';
import { WorkflowTriggerNode } from './nodes/models/workflow-trigger-node';
import { FormUpdatedEvent } from './tool-bars/detail-bar/core/form-updated-event';
import { DetailBarComponent } from './tool-bars/detail-bar/detail-bar.component';
import { MenuBarComponent } from './tool-bars/menu-bar/menu-bar.component';
type Schemes = GetSchemes<ClassicPreset.Node, ClassicPreset.Connection<ClassicPreset.Node, ClassicPreset.Node>>;

@Component({
    selector: 'app-designer',
    templateUrl: './designer.component.html',
    styleUrls: ['./designer.component.scss'],
    imports: [MatButtonModule, MatTooltipModule, MenuBarComponent, DetailBarComponent, ServerProgressBarSpinnerComponent],
})
export class DesignerComponent implements AfterViewInit {
    /**
     * Services utilisés dans le composant.
     */
    private toastrService = inject(ToastrService);

    /**
     * Service utilisé pour ouvrir des boites de dialogue.
     */
    public dialog: MatDialog = inject(MatDialog);

    /**
     * Liste des icones utilisés dans le template.
     */
    public faFolderPlus = faFolderPlus;
    public emailIcon = faEnvelope;
    public attributeIcon = faKeyboard;
    public delayExpiredIcon = faHourglassEnd;
    public dateReachIcon = faCalendar;
    public apiCallIcon = faCloud;
    public workflowCompleteIcon = faFlag;
    public attributeUpdateIcon = faTableList;
    public unreadIcon = faRotateLeft;

    @ViewChild(ServerProgressBarSpinnerComponent) serverProgressBarSpinner: ServerProgressBarSpinnerComponent;

    /**
     * Durée maximale par défaut d'un workflow.
     */
    workflowDefaultMaxDuration;

    /**
     * Contexte du designer de workflow.
     */
    context: ReteContext;

    /**
     * Gestionnaire de la selection des elements du designer de workflow.
     */
    selectionHandler = new SelectionHandler();

    /**
     * Gestionnaire des évènements du designer de workflow.
     */
    callbackHandler: DesignerCallbackHandler;

    /**
     * Wrapper de l'editeur Rete.js
     */
    editorWrapper: EditorWrapper;

    /**
     * Gestionnaire des noeuds
     */
    nodesManager: NodesManager;

    /**
     * Réference au conteneur affichant le designer.
     */
    @ViewChild('rete') container!: ElementRef;

    /**
     * Informations sur le mapping entre l'id des noeuds et les elements correspondants.
     */
    mappingInfos: MappingInfos;

    /**
     * Rapport de validité du workflow.
     */
    validityReport: WorkflowValidityReport = null;

    /**
     * Indique si l'organisation à souscrit un abonnement aux SMS.
     */
    smsSubscriptionEnabled: boolean = false;

    /**
     * Constructeur.
     * @param injector : Utilisé par l'injection de dépendance.
     */
    constructor(
        private injector: Injector,
        public workflowDefinitionsService: WorkflowDefinitionsService,
        public memoryService: MemoryService,
        private spinner: NgxSpinnerService,
        protected notificationService: NotificationService
    ) {
        this.mappingInfos = new MappingInfos();
        // Crée le gestionnaire de callback
        this.callbackHandler = new DesignerCallbackHandler(this.dialog, this.selectionHandler, this.mappingInfos);
        this.nodesManager = new NodesManager();
        this.nodesManager.callbackHandler = this.callbackHandler;
        this.smsSubscriptionEnabled = memoryService.getConnectedUser().hasServiceSubscription(ServiceCodes.HubSmsSending);
    }

    /**
     * Appellé lorsque la vue du composant a fini de s'initialiser.
     */
    ngAfterViewInit(): void {
        const el = this.container.nativeElement;

        if (el) {
            this.nodesManager.mappingInfos = this.mappingInfos;

            // Crée le Wrapper de l'editeur Rete.js
            this.editorWrapper = new EditorWrapper(this.injector, this.callbackHandler, this.mappingInfos);
            this.nodesManager.editorWrapper = this.editorWrapper;

            // Crée l'editeur Rete.js
            this.context = this.editorWrapper.createEditor(el);
            this.nodesManager.context = this.context;

            // Assigne le contexte au gestionnaire de sélection et au gestionnaire de callback
            this.callbackHandler.context = this.context;
            this.selectionHandler.context = this.context;
            this.nodesManager.selectionHandler = this.selectionHandler;

            this.registerMouseEvents();
            this.registerKeyboardEvents();
        }

        this.loadWorkflow();
    }

    /**
     * Abonnement aux événements du clavier.
     **/
    private registerKeyboardEvents() {
        document.addEventListener('keyup', async (e: KeyboardEvent) => {
            if (this.container.nativeElement.contains(e.target)) {
                if (e.code === 'Delete') {
                    // En cas d'appui sur la touche Delete alors qu'un noeud ou une connexion est selectionné, le noeud ou la connexion selectionné est supprimé.
                    // Tricks : La propriété target de l'évènement du clavier ne ramène pas l'élément selectionné mais l'element ayant le focus. Du coup il a fallut donné le focus au div contenant le noeud selectionné ou à l'élément SVG de la connexion selectionnée.

                    // Récupération du noeud selectionné
                    const nodes = this.context.editor.getNodes();
                    const selectedNode = nodes.find((node) => node.selected === true);

                    if (selectedNode) {
                        // Un noeud est selectionné --> suppression du noeud et des connexions rattachées au noeud.

                        this.removeNode(selectedNode);
                    } else {
                        // Aucun noeud n'est selectionné --> recupération de la connexion selectionnée
                        const connections = this.context.editor.getConnections();
                        const selectedConnection = connections.find((connection: SelectableConnection) => connection.selected === true);

                        if (selectedConnection) {
                            // Une connexion est selectionné --> suppression de la connexion
                            this.removeConnection(selectedConnection as SelectableConnection);
                        }
                    }
                }
            }
        });
    }

    /**
     * Supprime une connexion du designer de workflow.
     * @param connection : Instance de la connexion à supprimer.
     */
    private removeConnection(connection: SelectableConnection) {
        this.context.editor.removeConnection(connection.id);

        this.selectionHandler.clearSelection();
    }

    /**
     * Supprime un noeud du designer de workflow.
     * @param node : Instance du noeud à supprimer.
     */
    removeNode(node: Node) {
        if (node instanceof WorkflowStepNode) {
            if (this.isStepDeletionAllowed((node as WorkflowStepNode).workflowStepData.id) === false) {
                // La suppression est annulée
                return;
            }
        }

        // Récupération des connections rattachées à ce noeud.
        const connections = this.context.editor.getConnections();
        const nodeConnections = connections.filter((connection: SelectableConnection) => connection.source === node.id || connection.target === node.id);

        // Suppression des connections rattachées à ce noeud.
        nodeConnections.forEach((connection) => {
            console.log('removing connection', connection.id);
            setTimeout(() => {
                this.context.editor.removeConnection(connection.id);
            }, 10);
        });

        // Cas particulier du trigger de type minuteur --> suppression des actions Arrêt de minuteur associées
        if (node instanceof WorkflowTriggerNode) {
            const trigger = this.context.workflow.triggers.find((trigger) => trigger.id === (node as WorkflowTriggerNode).workflowTriggerData.id);

            if (trigger.type === WorkflowTriggerTypes.Timer) {
                this.removeRelatedTimerStopActions(trigger.id);
            }
        }

        // Suppression du noeud.
        this.context.editor.removeNode(node.id);
        this.selectionHandler.clearSelection();

        // Suppression de l'element dans le workflow
        if (node instanceof WorkflowStepNode) {
            this.context.workflow.steps = this.context.workflow.steps.filter((step) => step.id !== (node as WorkflowStepNode).workflowStepData.id);
        } else if (node instanceof WorkflowTriggerNode) {
            this.context.workflow.triggers = this.context.workflow.triggers.filter((trigger) => trigger.id !== (node as WorkflowTriggerNode).workflowTriggerData.id);
        } else if (node instanceof WorkflowActionNode) {
            this.context.workflow.actions = this.context.workflow.actions.filter((action) => action.id !== (node as WorkflowActionNode).workflowActionData.id);
        }
    }

    /**
     * Supprime les noeuds des actions de type 'Arrêt de minuteur' qui référence l'id du trigger de type 'Minuteur' spécifié.
     * @param timerTriggerId : Id d'un trigger de type 'Minuteur'.
     */
    private removeRelatedTimerStopActions(timerTriggerId: number) {
        const timerStopActions = this.context.workflow.actions.filter((action) => action.type === WorkflowActionTypes.TimerStop);

        const relatedTimerStopActions = timerStopActions.filter((action) => (action.data as TimerStopActionData).workflowTriggerId === timerTriggerId);

        relatedTimerStopActions.forEach((x) => {
            const actionNodeId = this.mappingInfos.actionNodeIdMappings.get(x.id);
            const actionNode = this.context.editor.getNodes().find((node) => node.id === actionNodeId);
            this.removeNode(actionNode);
            this.notificationService.displayWarning(
                'Suppression en cascade',
                `L'action '${x.id}' de type 'Arrêt de minuteur' a également été supprimée car elle referençait ce trigger.`
            );
        });
    }

    /**
     * Abonnement aux événements de la souris.
     **/
    private registerMouseEvents() {
        document.addEventListener('click', (e: PointerEvent) => {
            if (this.container.nativeElement === e.target) {
                // En cas de clic dans un espace vide du designer (autre qu'un noeud ou une connection), deselectionne le noeud ou la connexion actuellement selectionné.
                this.selectionHandler.clearSelection();
            }
        });
    }

    /**
     * Sauvegarde la configuration du workflow.
     */
    public saveWorkflow() {
        let workflow = new Workflow();
        workflow.maxDuration = this.context.workflow.maxDuration;
        workflow.isActive = this.context.workflow.isActive;
        this.context.editor.getNodes().forEach((node) => {
            this.saveNode(node, workflow);
        });

        (this.context.editor.getConnections() as SelectableConnection[]).forEach((connection) => {
            if (connection instanceof SelectableConnection) {
                this.saveConnection(connection, workflow);
            } else {
                console.log('Not a SelectableConnection');
                this.saveNewConnection(connection as ClassicPreset.Connection<ClassicPreset.Node, ClassicPreset.Node>, workflow);
            }
        });

        console.log('workflow.events', workflow.events);

        workflow.triggers = workflow.triggers.sort((x, y) => x.id - y.id);
        workflow.actions = workflow.actions.sort((x, y) => x.id - y.id);
        workflow.events = workflow.events.sort((x, y) => Number(x.id) - Number(y.id));
        workflow.steps = workflow.steps.sort((x, y) => Number(x.id) - Number(y.id));

        this.serverProgressBarSpinner.openProgressBarPanel();
        this.validityReport = null;
        this.workflowDefinitionsService
            .update(this.memoryService.getSelectedView()?.id, workflow)
            .pipe(
                catchError((err: HttpError) => {
                    console.log(err);
                    this.notificationService.displayError('Erreur de sauvegarde', err.message);
                    this.serverProgressBarSpinner.closeProgressBarPanel();
                    return of(null);
                })
            )
            .subscribe((x) => {
                this.spinner.hide('main-spinner');

                if (x.updatedWorkflow) {
                    if (x.validityReport.isValid) {
                        this.notificationService.displaySuccess('Sauvegarde effectuée', 'Le workflow a été sauvegardé.');
                    } else {
                        this.notificationService.displayWarning(
                            'Sauvegarde effectuée',
                            'Le workflow a été sauuvegardé mais il contient des erreurs. Vérifiez les erreurs de validation et corrigez les.'
                        );
                    }
                } else if (!x.validityReport.isValid) {
                    this.notificationService.displayError(
                        'Sauvegarde annulée',
                        "Un workflow actif ne peut pas être sauvegardé lorsqu'il contient des erreurs. Vérifiez les erreurs de validation et corrigez les."
                    );
                }

                this.validityReport = x.validityReport;
            });

        // let json = JSON.stringify(workflow);
        // localStorage.setItem('workflow', json);
    }

    /**
     * Active le workflow.
     */
    public activateWorkflow() {
        this.spinner.show('main-spinner');

        this.validityReport = null;
        this.workflowDefinitionsService
            .activate(this.memoryService.getSelectedView()?.id)
            .pipe(
                catchError((err: HttpError) => {
                    this.spinner.hide('main-spinner');
                    console.log(err);
                    this.notificationService.displayError('Erreur de validation', err.message);
                    return of(null);
                })
            )
            .subscribe((x) => {
                this.spinner.hide('main-spinner');

                if (x.updatedWorkflow) {
                    if (x.validityReport.isValid) {
                        this.notificationService.displaySuccess('Activation effectuée', 'Le workflow a été activé et peut être utilisé par les utilisateurs.');
                        this.context.workflow = x.updatedWorkflow;
                    }
                } else {
                    this.notificationService.displayError(
                        'Activation interdite',
                        "Un workflow ne peut pas être activé lorsqu'il contient des erreurs. Vérifiez les erreurs de validation et corrigez les."
                    );
                }

                this.validityReport = x.validityReport;
            });

        // let json = JSON.stringify(workflow);
        // localStorage.setItem('workflow', json);
    }

    /**
     * Charge le workflow à partir du localStorage du navigateur.
     * La première fois charge le workflow depuis le code s'il n'est pas présent dans le localStorage.
     */
    public loadWorkflow() {
        this.spinner.show('main-spinner');
        let workflow = this.workflowDefinitionsService.get(this.memoryService.getSelectedView()?.id).subscribe((x) => {
            this.context.workflow = x;
            this.context.editor.clear().then((ready) => {
                this.editorWrapper.displayWorkflow(x);
                this.spinner.hide('main-spinner');
            });
        });
    }

    /**
     * Emis lorsque l'utilisateur met à jour le formulaire d'un noeud ou d'une connexion.
     * @param event : évènement emis par le formulaire
     */
    public onFormUpdated(event: FormUpdatedEvent) {
        // Met à jour le workflow
        switch (event.type) {
            case 'node':
                switch (event.nodeType) {
                    case 'step':
                        let step = this.context.workflow.steps.find((x) => x.id === event.data.id);
                        step.data = event.data as StepData;
                        break;
                    case 'action':
                        let action = this.context.workflow.actions.find((x) => x.id === event.data.id);
                        action.data = event.data as AnyActionData;
                        break;
                    case 'trigger':
                        let trigger = this.context.workflow.triggers.find((x) => x.id === event.data.id);
                        trigger.data = event.data as AnyTriggerData;
                        break;
                }
                break;
            case 'connection':
                let evt = this.context.workflow.events.find((x) => x.id === event.data.id);
                evt.data = event.data as EventData;
        }

        // Met à jour l'affichage du noeud ou de la connexion.
        this.context.area.update(event.type, event.id);
    }

    /**
     * Appellée lorsque l'utilisateur clique sur le bouton "Définir l'ordre des étapes".
     */
    public sortSteps() {
        const dialogRef = this.dialog.open(OrderingStepDialogComponent, {
            maxWidth: '400px',
            data: {
                title: 'Ordre des étapes',
                items: this.context.workflow.steps,
            } as OrderingStepDialogData,
            panelClass: 'empty-dialog',
        });

        // Attente de la réponse de la boite de dialogue de saisie.
        dialogRef.afterClosed().subscribe((dialogResult: OrderingStepDialogData) => {
            if (dialogResult) {
                // Mise à jour de la variable.
                console.log('ordred steps', dialogResult.items);
                this.context.workflow.steps = dialogResult.items;
            }
        });
    }

    /**
     * Appellée lorsque l'utilisateur clique sur le bouton Rechercher de la boite de dialogue de recherche.
     */
    public onSearchClick() {
        const dialogRef = this.dialog.open(SearchDialogComponent, {
            maxWidth: '400px',
            panelClass: 'empty-dialog',
        });

        // Attente de la réponse de la boite de dialogue de recherche.
        dialogRef.afterClosed().subscribe((dialogResult: SearchDialogResult) => {
            if (dialogResult) {
                this.highlight(dialogResult.elementType, dialogResult.elementId);
            }
        });
    }

    public createAttributeChangedTriggerNodeInstance(): WorkflowTriggerNode {
        return new WorkflowTriggerNode(
            "Mise à jour d'attribut",
            WorkflowTriggerTypes.AttributeChanged,
            new AttributeChangedTriggerData(),
            this.callbackHandler.triggerNodeCallBack.bind(this.callbackHandler)
        );
    }

    public onElementVisibilityChanged(elementVisibility: ElementVisibility) {
        (this.context.editor.getConnections() as SelectableConnection[]).forEach((connection) => {
            if (elementVisibility.type === ElementVisibilityTypes.ActivationEvent && connection.targetSocket.label === 'Activation') {
                connection.visible = elementVisibility.visible;
            } else if (elementVisibility.type === ElementVisibilityTypes.CompleteEvent && connection.targetSocket.label === 'Finalisation') {
                connection.visible = elementVisibility.visible;
            } else if (
                elementVisibility.type === ElementVisibilityTypes.OtherEvent &&
                connection.targetSocket.label !== 'Activation' &&
                connection.targetSocket.label !== 'Finalisation'
            ) {
                connection.visible = elementVisibility.visible;
            }
        });
    }

    /**
     * Appellée lorsque l'utilisateur clique sur le bouton "Paramètres du workflow".
     */
    public onManageSettingsClick() {
        const dialogRef = this.dialog.open(WorkflowSettingsDialogComponent, {
            maxWidth: '400px',
            data: {
                title: 'Paramètres du workflow',
                maxDuration: this.context.workflow.maxDuration,
            } as WorkflowSettingsDialogData,
            panelClass: 'empty-dialog',
        });

        // Attente de la réponse de la boite de dialogue de saisie.
        dialogRef.afterClosed().subscribe((dialogResult: WorkflowSettingsDialogData) => {
            if (dialogResult) {
                // Mise à jour des paramètres du workflow.
                console.log('Save workflow settings', dialogResult);
                this.context.workflow.maxDuration = dialogResult.maxDuration;
            }
        });
    }

    /**
     * Appellée lorsque l'utilisateur clique sur le bouton "Recentrer l'affichage".
     */
    public onRecenterClick() {
        this.editorWrapper.recenter();
    }

    /**
     * Appellée lorsque l'utilisateur clique sur le bouton "Réorganiser l'affichage".
     */
    public onRearrangeClick() {
        this.editorWrapper.rearrange();
    }

    /**
     * Appellée lorsque l'utilisateur clique sur un message d'avertissement ou un message d'erreur de la validation pour selectionner l'élément à l'origine de l'avertissement ou de l'erreur.
     * @param sourceType : Type de l'élément à l'origine de l'avertissement ou de l'erreur.
     * @param sourceId : Id de l'élément à l'origine de l'avertissement ou de l'erreur.
     */
    public onValidityElementClick(sourceType: WorkflowElementType, sourceId: number) {
        this.highlight(sourceType, sourceId);
    }

    /**
     * Met en evidence un élément du workflow.
     * Centre la vue sur l'élément et effectue un zoom.
     * @param elementType : Type de l'élément.
     * @param elementId : Id de l'élément.
     */
    private highlight(elementType: WorkflowElementType, elementId: number) {
        console.log('highlight');
        switch (elementType) {
            case WorkflowElementType.Action:
                const actionNodeId = this.mappingInfos.actionNodeIdMappings.get(elementId);
                const actionNode = this.context.editor.getNodes().find((x) => x.id === actionNodeId);

                if (actionNode) {
                    this.selectionHandler.selectNode(actionNode);
                    this.editorWrapper.centerOnNode(actionNode);
                } else {
                    this.notificationService.displayWarning('Action non trouvée.', `Aucune action ne correspond à l\'id ${elementId}`);
                }
                break;
            case WorkflowElementType.Trigger:
                const triggerNodeId = this.mappingInfos.triggerNodeIdMappings.get(elementId);
                const triggerNode = this.context.editor.getNodes().find((x) => x.id === triggerNodeId);
                if (triggerNode) {
                    this.selectionHandler.selectNode(triggerNode);
                    this.editorWrapper.centerOnNode(triggerNode);
                } else {
                    this.notificationService.displayWarning('Trigger non trouvé.', `Aucun trigger ne correspond à l\'id ${elementId}`);
                }
                break;
            case WorkflowElementType.Step:
                const stepNodeId = this.mappingInfos.getStepNodeIdByStepId(elementId);
                const stepNode = this.context.editor.getNodes().find((x) => x.id === stepNodeId);
                if (stepNode) {
                    this.selectionHandler.selectNode(stepNode);
                    this.editorWrapper.centerOnNode(stepNode);
                } else {
                    this.notificationService.displayWarning('Etape non trouvée.', `Aucune étape ne correspond à l\'id ${elementId}`);
                }
                break;
            case WorkflowElementType.Event:
                const connection = this.context.editor.getConnections().find((x) => (x as SelectableConnection).workflowEventData.id === elementId) as SelectableConnection;
                if (connection) {
                    this.selectionHandler.selectConnection(connection);
                    const sourceNode = this.context.editor.getNodes().find((x) => x.id === connection.source);
                    const targetNode = this.context.editor.getNodes().find((x) => x.id === connection.target);
                    this.editorWrapper.centerOnNodes([sourceNode, targetNode]);
                } else {
                    this.notificationService.displayWarning('Evenement non trouvé.', `Aucun évenement ne correspond à l\'id ${elementId}`);
                }
                break;
        }
    }

    /**
     * Sauvegarde une connexion du designer en tant qu'évènement dans le workflow.
     * @param node : Connexion à sauvegarder.
     * @param workflow : Workflow dans lequel sauvegarder la connexion.
     */
    private saveConnection(connection: SelectableConnection, workflow: Workflow) {
        const event = new Event(
            connection.workflowEventData.id,
            Number(connection.sourceSocket.code),
            Number(connection.targetSocket.code),
            connection.sourceSocket,
            connection.targetSocket,
            connection.workflowEventData
        );
        workflow.events.push(event);
    }

    /**
     * Sauvegarde une connexion du designer en tant qu'évènement dans le workflow.
     * @param node : Connexion à sauvegarder.
     * @param workflow : Workflow dans lequel sauvegarder la connexion.
     */
    private saveNewConnection(connection: ClassicPreset.Connection<ClassicPreset.Node, ClassicPreset.Node>, workflow: Workflow) {
        // On recherche s'il s'agit d'un trigger d'une étape
        let triggerId = this.mappingInfos.getStepNodeTriggerId(connection.source, connection.sourceOutput);

        if (!triggerId) {
            // Sinon --> c'est qu'il s'agit d'un trigger "classique".
            triggerId = this.mappingInfos.getNodeTriggerId(connection.source);
        }

        // On recherche s'il s'agit d'une action d'une étape
        let actionId = this.mappingInfos.getStepNodeActionId(connection.target, connection.targetInput);

        if (!actionId) {
            // Sinon --> c'est qu'il s'agit d'une action "classique".
            actionId = this.mappingInfos.getNodeActionId(connection.target);
        }

        const triggerNode = this.context.editor.getNodes().find((x) => x.id === connection.source);
        const actionNode = this.context.editor.getNodes().find((x) => x.id === connection.target);

        const triggerSocket = triggerNode.outputs[connection.sourceOutput];
        const actionSocket = actionNode.inputs[connection.targetInput];

        const event = new Event(
            this.editorWrapper.generateNewEventId(),
            triggerId,
            actionId,
            { code: triggerId.toString(), label: triggerSocket.label ? triggerSocket.label : 'output' },
            { code: actionId.toString(), label: actionSocket.label ? actionSocket.label : 'input' },
            (connection as any).workflowEventData
        );
        workflow.events.push(event);
        console.log('Save Event', event.id, `${event.triggerId} (${event.triggerSocket.label}) --> ${event.actionId} (${event.actionSocket.label})`);
    }

    /**
     * Sauvegarde un noeud du designer en tant que trigger, action ou étape dans le workflow.
     * @param node : Noeud à sauvegarder.
     * @param workflow : Workflow dans lequel sauvegarder le noeud.
     */
    private saveNode(
        node: ClassicPreset.Node<{ [x: string]: ClassicPreset.Socket }, { [x: string]: ClassicPreset.Socket }, { [x: string]: ClassicPreset.Control }>,
        workflow: Workflow
    ) {
        const nodeView = this.context.area.nodeViews.get(node.id);

        if (node instanceof WorkflowStepNode) {
            this.saveStepNode(node, nodeView, workflow, this.context.editor.getConnections() as SelectableConnection[]);
        } else if (node instanceof WorkflowActionNode) {
            this.saveActionNode(node, nodeView, workflow);
        } else if (node instanceof WorkflowTriggerNode) {
            this.saveTriggerNode(node, nodeView, workflow);
        }
    }

    /**
     * Sauvegarde un noeud de type Trigger du designer en tant que Trigger dans le workflow.
     * @param node : Noeud à sauvegarder.
     * @param nodeView : Rendu du noeud.
     * @param workflow : Workflow dans lequel sauvegarder le noeud.
     */
    private saveTriggerNode(node: WorkflowTriggerNode, nodeView, workflow: Workflow) {
        const trigger = new Trigger((node as WorkflowTriggerNode).type, nodeView.position);
        trigger.id = this.mappingInfos.getNodeTriggerId(node.id);
        trigger.data = (node as WorkflowTriggerNode).workflowTriggerData;
        workflow.triggers.push(trigger);
    }

    /**
     * Sauvegarde un noeud de type Action du designer en tant qu'Action dans le workflow.
     * @param node : Noeud à sauvegarder.
     * @param nodeView : Rendu du noeud.
     * @param workflow : Workflow dans lequel sauvegarder le noeud.
     */
    private saveActionNode(node: WorkflowActionNode, nodeView, workflow: Workflow) {
        const action = new Action((node as WorkflowActionNode).type, nodeView.position);
        action.id = this.mappingInfos.getNodeActionId(node.id);
        action.data = (node as WorkflowActionNode).workflowActionData;
        workflow.actions.push(action);
        console.log('Save Action Node', action.id, action.data);
    }

    /**
     * Sauvegarde un noeud de type Etape du designer en tant qu'étape dans le workflow.
     * @param node : Noeud à sauvegarder.
     * @param nodeView : Rendu du noeud.
     * @param workflow : Workflow dans lequel sauvegarder le noeud.
     */
    private saveStepNode(node: WorkflowStepNode, nodeView, workflow: Workflow, connections: SelectableConnection[]) {
        const inputs = Object.keys(node.inputs).map((x) => node.inputs[x].label);
        const outputs = Object.keys(node.outputs).map((x) => node.outputs[x].label);

        const step = new Step(this.mappingInfos.stepNodeIdMappings.get(node.id), node.label, inputs, outputs, nodeView.position, node.workflowStepData);
        workflow.steps.push(step);

        connections.forEach((c) => {
            console.log(`Saving connection${c.id} [${c.source} --> ${c.target}]`);
            if (c.source === node.id) {
                // Save trigger
                //const actionId = this.editorWrapper.getNodeActionId(c.target);
                this.saveStepNodeOutputs(node, Number(c.sourceOutput), step, nodeView, workflow);
            }

            if (c.target === node.id) {
                // Save action
                //const triggerId = this.editorWrapper.getNodeTriggerId(c.source);
                this.saveStepNodeInputs(node, Number(c.targetInput), step, nodeView, workflow);
            }
        });

        // Object.getOwnPropertyNames(node.inputs).forEach((inputKey) => {
        //     this.saveStepNodeInputs(node, inputKey, step, nodeView, workflow);
        // });

        // Object.getOwnPropertyNames(node.outputs).forEach((outputKey) => {
        //     this.saveStepNodeOutputs(node, outputKey, step, nodeView, workflow);
        // });
    }

    /**
     * Sauvegarde les outputs d'un noeud étape sous forme de triggers.
     * @param node : Noeud de l'étape.
     * @param outputKey : Clé de la socket de sortie correspondant au trigger.
     * @param step : Instance de l'étape.
     * @param nodeView : Rendu du noeud.
     * @param workflow : Workflow dans lequel sauvegarder les triggers relatifs au noeud.
     */
    private saveStepNodeOutputs(node: WorkflowStepNode, outputKey: number, step: Step, nodeView: any, workflow: Workflow) {
        // const output = node.outputs[outputKey];
        // let type: WorkflowTriggerTypes = null;
        // let data: StepTriggerData = null;

        let trigger = this.context.workflow.triggers.find((x) => x.id === outputKey);

        // if (output.label === 'Activée') {
        //     type = WorkflowTriggerTypes.StepActivated;
        //     let stepActivatedTriggerData = new StepActivatedTriggerData();
        //     stepActivatedTriggerData.stepId = step.id;
        //     data = stepActivatedTriggerData;
        // } else if (output.label === 'Finalisée') {
        //     type = WorkflowTriggerTypes.StepCompleted;
        //     let stepCompletedTriggerData = new StepCompletedTriggerData();
        //     stepCompletedTriggerData.stepId = step.id;
        //     data = stepCompletedTriggerData;
        // } else {
        //     type = WorkflowTriggerTypes.StepStateChanged;
        //     let stepCompletedTriggerData = new StepStateChangedTriggerData();
        //     stepCompletedTriggerData.stepId = step.id;
        //     var regex = /Etat à '([^']+)'/g;
        //     let matches = regex.exec(output.label);
        //     const stateName = matches.slice(1)[0];
        //     const stateId = step.data.states.find((x) => x.name === stateName).id;
        //     stepCompletedTriggerData.stateId = stateId;
        //     data = stepCompletedTriggerData;
        // }

        // const trigger = new Trigger(type, nodeView.position);
        // trigger.id = Number(outputKey);
        // trigger.data = data;

        if (!workflow.triggers.find((x) => x.id === trigger.id)) {
            workflow.triggers.push(trigger);
            console.log('Save Step Node output', trigger.id, trigger.data, node.id);
        } else {
            console.log('Skip saving Step Node output', trigger.id, trigger.data, node.id);
        }
    }

    /**
     * Sauvegarde les inputs d'un noeud étape sous forme d'actions.
     * @param node : Noeud de l'étape.
     * @param inputKey : Clé de la socket d'entrée correspondant à l'action.
     * @param step : Instance de l'étape.
     * @param nodeView : Rendu du noeud.
     * @param workflow : Workflow dans lequel sauvegarder les actions relatives au noeud.
     */
    private saveStepNodeInputs(node: WorkflowStepNode, inputKey: number, step: Step, nodeView: any, workflow: Workflow) {
        // const input = node.inputs[inputKey];
        // let type: WorkflowActionTypes = null;
        // let data: StepActionData = null;

        let action = this.context.workflow.actions.find((x) => x.id === inputKey);

        // if (input.label === 'Activation') {
        //     type = WorkflowActionTypes.ActivateStep;
        //     let activateStepActionData = new ActivateStepActionData();
        //     activateStepActionData.stepId = step.id;
        //     activateStepActionData.stateId = step.id;
        //     data = activateStepActionData;
        // } else if (input.label === 'Finalisation') {
        //     type = WorkflowActionTypes.CompleteStep;
        //     let completeStepActionData = new CompleteStepActionData();
        //     completeStepActionData.stepId = step.id;
        //     data = completeStepActionData;
        // } else {
        //     type = WorkflowActionTypes.ChangeStepState;
        //     let changeStepStateActionData = new ChangeStepStateActionData();
        //     changeStepStateActionData.stepId = step.id;
        //     var regex = /Changer l'état à '([^']+)'/g;
        //     let matches = regex.exec(input.label);
        //     const stateName = matches.slice(1)[0];
        //     const stateId = step.data.states.find((x) => x.name === stateName).id;
        //     changeStepStateActionData.stateId = stateId;
        //     // TODO Designer : Option à mettre au niveau de l'étape ?
        //     //changeStepStateActionData.activateStepIfCompleted = ?;
        //     //changeStepStateActionData.activateStepIfNotStarted = ?;
        //     data = changeStepStateActionData;
        // }

        // const action2 = new Action(type, nodeView.position);
        // action2.id = Number(inputKey);
        // action2.data = data;

        if (!workflow.actions.find((x) => x.id === action.id)) {
            workflow.actions.push(action);
            console.log('Save Step Node input', action.id, action.data, node.id);
        } else {
            console.log('Skip saving Step Node input', action.id, action.data, node.id);
        }
    }

    /**
     * Appelée pour déterminer si la suppression d'une étape est permise.
     * @param stepId : Id de l'étape à supprimer.
     * @returns True si la suppression de l'étape est permise; false autrement.
     */
    private isStepDeletionAllowed(stepId: number): boolean {
        return this.checkStepUseByAnyTrigger(stepId) === false && this.checkStepUseByAnyAction(stepId) === false && this.checkStepUseByAnyEventCondition(stepId) === false;
    }

    /**
     * Vérifie l'utilisation d'une étape par des triggers du workflow.
     * @param stepId : Id de l'étape à vérifier.
     * @returns True si au moins un trigger utilise l'étape.
     */
    private checkStepUseByAnyTrigger(stepId: number): boolean {
        for (let trigger of this.context.workflow.triggers) {
            if (trigger.type === WorkflowTriggerTypes.DelayExpired) {
                if ((trigger.data as ExpiredDelayTriggerData).stepId === stepId) {
                    this.notificationService.displayWarning(
                        'Suppression impossible',
                        `La suppression de cette étape n'est pas permise car elle est referencée par le trigger ${trigger.id} de type 'Délai expiré'.`
                    );
                    return true;
                }
            }

            if (trigger.type === WorkflowTriggerTypes.Timer) {
                if ((trigger.data as TimerTriggerData).stepId === stepId) {
                    this.notificationService.displayWarning(
                        'Suppression impossible',
                        `La suppression de cette étape n'est pas permise car elle est referencée par le minuteur ${trigger.id}.`
                    );
                    return true;
                }
            }
        }

        return false;
    }

    /**
     * Vérifie l'utilisation d'une étape par des actions du workflow.
     * @param stepId : Id de l'étape à vérifier.
     * @returns True si au moins une action utilise l'étape.
     */
    private checkStepUseByAnyAction(stepId: number): boolean {
        return false;
    }

    /**
     * Vérifie l'utilisation d'une étape par des conditions d'évènement du workflow.
     * @param stepId : Id de l'étape à vérifier.
     * @returns True si au moins une condition d'évènement utilise l'étape.
     */
    private checkStepUseByAnyEventCondition(stepId: number): boolean {
        for (let events of this.context.workflow.events) {
            for (let condition of events.data?.conditions.filter(
                (x) => x.type === WorkflowConditionTypes.StepSpecificState || x.type === WorkflowConditionTypes.StepActivated || x.type === WorkflowConditionTypes.StepCompleted
            )) {
                let error = false;
                switch (condition.type) {
                    case WorkflowConditionTypes.StepSpecificState:
                        if ((condition as StepSpecificStateConditionData).stepId === stepId) {
                            error = true;
                        }
                        break;
                    case WorkflowConditionTypes.StepActivated:
                        if ((condition as ActivatedStepsConditionData).steps.includes(stepId) === true) {
                            error = true;
                        }
                        break;
                    case WorkflowConditionTypes.StepCompleted:
                        if ((condition as CompletedStepsConditionData).steps.includes(stepId) === true) {
                            error = true;
                        }
                        break;
                }

                if (error === true) {
                    this.notificationService.displayWarning(
                        'Suppression impossible',
                        `La suppression de l'étape '${this.context.workflow.steps.find((x) => x.id === stepId).data.stepName}' n'est pas permise car elle est referencée par une condition d'évènement d'étape.`
                    );
                    return true;
                }
            }
        }

        return false;
    }
}
